import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import {
  EMPTY_ONLINE_BOOKING_LEGAL,
  onlineBookingLegalFromApi,
  type OnlineBookingLegalForm,
  type OnlineBookingLegalSettings,
} from "@/pages/stammdaten/onlineBookingLegalTypes";

interface SettingsResponse {
  online_booking_legal: OnlineBookingLegalSettings;
}

interface OnlineBookingLegalSectionProps {
  canWrite: boolean;
}

export function OnlineBookingLegalSection({ canWrite }: OnlineBookingLegalSectionProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [legal, setLegal] = useState<OnlineBookingLegalForm>(EMPTY_ONLINE_BOOKING_LEGAL);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<SettingsResponse>("/api/v1/settings")
      .then((settings) => {
        setLegal(onlineBookingLegalFromApi(settings.online_booking_legal));
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (!canWrite) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await api.patch<SettingsResponse>("/api/v1/settings/online-booking-legal", {
        responsible_name: legal.responsible_name.trim() || null,
        legal_representative: legal.legal_representative.trim() || null,
        contact_email: legal.contact_email.trim() || null,
        contact_phone: legal.contact_phone.trim() || null,
        privacy_email: legal.privacy_email.trim() || null,
        supervisory_authority: legal.supervisory_authority.trim() || null,
        imprint_url: legal.imprint_url.trim() || null,
        retention_note: legal.retention_note.trim() || null,
        third_party_note: legal.third_party_note.trim() || null,
      });
      setSuccess(true);
      load();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Rechtliche Angaben konnten nicht gespeichert werden",
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card
      id="rechtliches"
      className="scroll-mt-24"
      title="Rechtliche Angaben (Online-Buchung)"
      description="Erscheinen in den Stornobedingungen und Datenschutzhinweisen der Online-Buchungsstrecke. Name und Anschrift des Betreibers werden aus dem Briefkopf oben übernommen, sofern Sie keinen abweichenden Verantwortlichen angeben."
      bodyClassName="space-y-4"
    >
      {error ? <p className="text-sm text-status-red">{error}</p> : null}
      {loading ? (
        <p className="text-sm text-label-secondary">Laden…</p>
      ) : (
        <form onSubmit={(e) => void onSave(e)} className="space-y-3">
          <fieldset className="grid gap-3 sm:grid-cols-2" disabled={!canWrite}>
            <label className="flex flex-col gap-1 text-sm sm:col-span-2">
              Verantwortlicher (optional)
              <input
                className="eh-input"
                placeholder="Leer = Name des Campingplatzes aus dem Briefkopf"
                value={legal.responsible_name}
                onChange={(e) =>
                  setLegal((prev) => ({ ...prev, responsible_name: e.target.value }))
                }
              />
            </label>
            <label className="flex flex-col gap-1 text-sm sm:col-span-2">
              Vertretungsberechtigte Person (optional)
              <input
                className="eh-input"
                placeholder="z. B. Geschäftsführung oder Inhaber/in"
                value={legal.legal_representative}
                onChange={(e) =>
                  setLegal((prev) => ({ ...prev, legal_representative: e.target.value }))
                }
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              E-Mail für Buchungen &amp; Stornierungen
              <input
                type="email"
                className="eh-input"
                placeholder="buchung@beispiel.de"
                value={legal.contact_email}
                onChange={(e) =>
                  setLegal((prev) => ({ ...prev, contact_email: e.target.value }))
                }
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Telefon für Stornierungen
              <input
                type="tel"
                className="eh-input"
                placeholder="+49 …"
                value={legal.contact_phone}
                onChange={(e) =>
                  setLegal((prev) => ({ ...prev, contact_phone: e.target.value }))
                }
              />
            </label>
            <label className="flex flex-col gap-1 text-sm sm:col-span-2">
              Separate Datenschutz-E-Mail (optional)
              <input
                type="email"
                className="eh-input"
                placeholder="Leer = E-Mail für Buchungen & Stornierungen"
                value={legal.privacy_email}
                onChange={(e) =>
                  setLegal((prev) => ({ ...prev, privacy_email: e.target.value }))
                }
              />
            </label>
            <label className="flex flex-col gap-1 text-sm sm:col-span-2">
              Zuständige Datenschutz-Aufsichtsbehörde (optional)
              <input
                className="eh-input"
                placeholder="z. B. Der Hessische Beauftragte für Datenschutz und Informationsfreiheit"
                value={legal.supervisory_authority}
                onChange={(e) =>
                  setLegal((prev) => ({ ...prev, supervisory_authority: e.target.value }))
                }
              />
            </label>
            <label className="flex flex-col gap-1 text-sm sm:col-span-2">
              Impressum-URL (optional)
              <input
                type="url"
                className="eh-input"
                placeholder="https://…/impressum"
                value={legal.imprint_url}
                onChange={(e) =>
                  setLegal((prev) => ({ ...prev, imprint_url: e.target.value }))
                }
              />
            </label>
            <label className="flex flex-col gap-1 text-sm sm:col-span-2">
              Zusatzhinweis Speicherdauer (optional)
              <textarea
                className="eh-input min-h-[4rem] resize-y"
                rows={3}
                placeholder="Ergänzender Hinweis zur Speicherdauer, z. B. steuerliche Aufbewahrungsfristen"
                value={legal.retention_note}
                onChange={(e) =>
                  setLegal((prev) => ({ ...prev, retention_note: e.target.value }))
                }
              />
            </label>
            <label className="flex flex-col gap-1 text-sm sm:col-span-2">
              Zusatzhinweis Weitergabe an Dritte (optional)
              <textarea
                className="eh-input min-h-[4rem] resize-y"
                rows={3}
                placeholder="z. B. Hosting-Anbieter, E-Mail-Dienst, Zahlungsabwicklung"
                value={legal.third_party_note}
                onChange={(e) =>
                  setLegal((prev) => ({ ...prev, third_party_note: e.target.value }))
                }
              />
            </label>
          </fieldset>
          {canWrite ? (
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Button type="submit" disabled={saving}>
                {saving ? "Speichern…" : "Speichern"}
              </Button>
              {success ? (
                <p className="text-sm text-status-green">Rechtliche Angaben gespeichert.</p>
              ) : null}
            </div>
          ) : null}
        </form>
      )}
    </Card>
  );
}
